import type { LayerFieldDetail, LayerRasterSettings, LayerWmtsSettings, StyleRule, StyleSymbolizer } from '../lib/layersApi';

export type DemoFeatureProps = {
  name?: string;
  objectType?: string;
  registryNumber?: string;
  address?: string;
  areaM2?: number;
  updatedAt?: string;
  [key: string]: unknown;
};

type Polygon = {
  type: 'Polygon';
  coordinates: number[][][];
};

type AnyGeometry = {
  type: string;
  coordinates?: unknown;
};

type Feature = {
  type: 'Feature';
  geometry: Polygon | AnyGeometry;
  properties: DemoFeatureProps;
};

type FeatureCollection = {
  type: 'FeatureCollection';
  features: Feature[];
};

export type LayerConfig = {
  id: string;
  name: string;
  color: string;
  opacity?: number;
  count: number;
  defaultVisible: boolean;
  warning?: string;
  /** Absent for WMS layers — those render as raster tiles (see `wms` below),
   * not as OpenLayers vector features. */
  geojson?: FeatureCollection;
  /** Present only for source==='wms' layers: renders as a WMS raster tile
   * layer (ol/source/TileWMS) instead of a vector layer built from `geojson`. */
  wms?: { url: string; layer: string; version: string; format: string };
  /** Present only for source==='wmts' layers: renders via the same resolved
   * tile matrix set logic as a WMTS basemap (see map/basemaps.ts), just as an
   * independently toggleable layer instead of the single active background. */
  wmts?: LayerWmtsSettings;
  /** Present only for source==='raster' layers: a plain tile-URL-template raster
   * (XYZ/TMS/MBTiles-served-as-XYZ/ArcGIS tiles), rendered via ol/source/XYZ. */
  raster?: LayerRasterSettings;
  /** True for layers persisted on the backend (via the import wizard) — these can be
   * edited/deleted through layersApi. Mock/OGC-registry layers and demo data don't have it. */
  isReal?: boolean;
  /** True for layers too large to load in one shot (see realLayers.ts's
   * LAZY_FEATURE_THRESHOLD) — `geojson` starts empty and MapContext's lazy-layer effect
   * fills it in scoped to the current view, refetching on pan/zoom instead of pulling
   * every feature up front. */
  lazy?: boolean;
  /** Matches a LayerGroup.name from AdminDataContext — null/undefined means ungrouped. */
  group?: string | null;
  /** Raw geometry type string — backend layers use lowercase codes (point/line/polygon/…),
   * mock layers use mixed vocabularies (Russian labels or GeoJSON type names). Normalize
   * with geometryKind() before comparing. */
  geometryType?: string;
  /** Layer publish status: published/draft/review/error — same vocabulary for real and mock layers. */
  status?: string;
  /** Field codes allowed to show in the object popup (from layer_fields.is_visible_in_popup).
   * Undefined means no filtering (mock/demo layers, which have no field semantics). */
  popupFieldCodes?: string[];
  /** Backend-computed feature-edit permission (LayerDto.can_edit_features) — gates the
   * drawing tool entry point. Undefined for mock/demo layers, treated as not editable. */
  canEditFeatures?: boolean;
  /** Field schema (data_type, dictionary_id, etc.) — lets the selected-object popup
   * render a 'dictionary'-typed property as a link instead of a raw value/id. */
  fields?: LayerFieldDetail[];
  /** feature id -> photo_file_id. A feature's photo isn't part of its GeoJSON
   * `properties` (it's a fixed object attribute, not an attribute field), so
   * OL's GeoJSON reader drops it — this sidecar map is how the map popup
   * looks it up after a click, keyed by the same id OL exposes via getId(). */
  photoByFeatureId?: Record<string, string | null>;
  /** Point/polygon marker rendering — 'icon' with no markerIconUrl behaves the
   * same as 'color' (styleForLayer's plain circle/fill), same as the backend. */
  markerMode?: 'color' | 'icon';
  markerIconUrl?: string;
  /** Set only when a SLD/QML style import produced real per-attribute rules
   * (see style_import.py) — 'simple' (or undefined) means render with the flat
   * color/opacity/markerMode fields above, same as before this existed. */
  styleType?: 'simple' | 'rule_based';
  styleRules?: StyleRule[];
  styleFallback?: StyleSymbolizer | null;
};

export type GeometryKind = 'point' | 'line' | 'polygon' | 'other';

/** Normalizes the many geometry-type vocabularies in use (backend lowercase codes,
 * Russian labels on seeded mock layers, GeoJSON type names on OGC mock layers) into
 * one set the layer-panel filter can compare against. */
export function geometryKind(raw: string | undefined | null): GeometryKind {
  if (!raw) return 'other';
  const s = raw.toLowerCase();
  if (s.includes('point') || s.includes('точ')) return 'point';
  if (s.includes('line') || s.includes('лини')) return 'line';
  if (s.includes('polygon') || s.includes('полиг')) return 'polygon';
  return 'other';
}

